/**
 * Entité Account - Représente un compte bancaire
 *
 * Règles métier:
 * - Un compte a au moins un titulaire
 * - Un compte joint a au moins deux titulaires
 * - Seul un compte actif peut effectuer des opérations
 * - Les retraits nécessitent au moins un titulaire actif
 */

import { AccountNumber } from '../value-objects/AccountNumber';

export enum AccountStatus {
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED',
  CLOSED = 'CLOSED',
}

export enum AccountType {
  CURRENT = 'CURRENT',       // Compte Courant
  SAVINGS = 'SAVINGS',       // Compte Épargne
  JOINT = 'JOINT',           // Compte Joint
}

export interface AccountHolder {
  readonly id: string;
  readonly name: string;
  readonly status: 'active' | 'inactive';
  readonly isPrimary: boolean;
}

export interface AccountProps {
  readonly accountNumber: AccountNumber;
  readonly type: AccountType;
  readonly status: AccountStatus;
  readonly holders: AccountHolder[];
  readonly createdAt: Date;
  readonly currency: 'XOF';
}

export class Account {
  private constructor(private readonly props: AccountProps) {
    Object.freeze(this.props);
    Object.freeze(this);
  }

  /**
   * Crée une nouvelle instance Account avec validation
   */
  static create(props: AccountProps): Account {
    // Validation: au moins un titulaire
    if (props.holders.length === 0) {
      throw new Error('Un compte doit avoir au moins un titulaire');
    }

    // Validation: compte joint doit avoir au moins 2 titulaires
    if (props.type === AccountType.JOINT && props.holders.length < 2) {
      throw new Error('Un compte joint doit avoir au moins deux titulaires');
    }

    // Validation: exactement un titulaire principal
    const primaryHolders = props.holders.filter(h => h.isPrimary);
    if (primaryHolders.length !== 1) {
      throw new Error('Un compte doit avoir exactement un titulaire principal');
    }

    return new Account(props);
  }

  /**
   * Reconstitution depuis persistence
   */
  static reconstitute(props: AccountProps): Account {
    return new Account(props);
  }

  // Getters
  get accountNumber(): AccountNumber {
    return this.props.accountNumber;
  }

  get type(): AccountType {
    return this.props.type;
  }

  get status(): AccountStatus {
    return this.props.status;
  }

  get holders(): readonly AccountHolder[] {
    return Object.freeze([...this.props.holders]);
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get currency(): 'XOF' {
    return this.props.currency;
  }

  /**
   * Retourne le titulaire principal
   */
  get primaryHolder(): AccountHolder {
    return this.props.holders.find(h => h.isPrimary)!;
  }

  /**
   * Vérifie si le compte est actif
   */
  isActive(): boolean {
    return this.props.status === AccountStatus.ACTIVE;
  }

  /**
   * Vérifie si le compte est bloqué
   */
  isBlocked(): boolean {
    return this.props.status === AccountStatus.BLOCKED;
  }

  /**
   * Vérifie si le compte est fermé
   */
  isClosed(): boolean {
    return this.props.status === AccountStatus.CLOSED;
  }

  /**
   * Vérifie si c'est un compte joint
   */
  isJoint(): boolean {
    return this.props.type === AccountType.JOINT;
  }

  /**
   * Vérifie si le compte peut effectuer un retrait
   * Conditions: compte actif + au moins un titulaire actif
   */
  canWithdraw(): boolean {
    return this.isActive() && this.hasActiveHolder();
  }

  /**
   * Vérifie si le compte peut recevoir un versement
   * Condition: compte actif
   */
  canReceiveDeposit(): boolean {
    return this.isActive();
  }

  /**
   * Vérifie si le compte a au moins un titulaire actif
   */
  hasActiveHolder(): boolean {
    return this.props.holders.some(h => h.status === 'active');
  }

  /**
   * Vérifie si un client est titulaire du compte
   */
  isHolder(clientId: string): boolean {
    return this.props.holders.some(h => h.id === clientId);
  }

  /**
   * Vérifie si un client est autorisé (titulaire + actif)
   */
  isHolderAuthorized(clientId: string): boolean {
    const holder = this.props.holders.find(h => h.id === clientId);
    return holder !== undefined && holder.status === 'active';
  }

  /**
   * Récupère un titulaire par son ID
   */
  getHolder(clientId: string): AccountHolder | undefined {
    return this.props.holders.find(h => h.id === clientId);
  }

  /**
   * Récupère tous les titulaires actifs
   */
  getActiveHolders(): AccountHolder[] {
    return this.props.holders.filter(h => h.status === 'active');
  }

  /**
   * Récupère tous les co-titulaires (excluant le principal)
   */
  getCoHolders(): AccountHolder[] {
    return this.props.holders.filter(h => !h.isPrimary);
  }

  /**
   * Vérifie l'égalité avec un autre compte
   */
  equals(other: Account): boolean {
    return this.props.accountNumber.equals(other.props.accountNumber);
  }

  /**
   * Sérialise pour persistence
   */
  toJSON(): Record<string, unknown> {
    return {
      accountNumber: this.props.accountNumber.toJSON(),
      type: this.props.type,
      status: this.props.status,
      holders: this.props.holders,
      createdAt: this.props.createdAt.toISOString(),
      currency: this.props.currency,
    };
  }

  /**
   * Désérialise depuis persistence
   */
  static fromJSON(json: Record<string, any>): Account {
    return Account.reconstitute({
      accountNumber: AccountNumber.fromJSON(json.accountNumber),
      type: json.type as AccountType,
      status: json.status as AccountStatus,
      holders: json.holders,
      createdAt: new Date(json.createdAt),
      currency: json.currency,
    });
  }
}
